<template>
  <div class="terceiros-envolvidos table-responsive position-relative">
    <div v-if="!modoVisualizacao" class="terceiros-envolvidos__include">
      <b-button
        variant="primary"
        :disabled="isLoading"
        @click="$emit('novo')"
        data-tooltip-location="left"
        data-tooltip="Incluir Envolvido"
        class="terceiros-envolvidos__include-button"
      >
        <i class="mdi mdi-account-plus-outline me-2 font-size-17 font-bold"></i>
        <span>Incluir Envolvido</span>
      </b-button>
    </div>

    <!-- Tabela Desktop - sempre visível (header mesmo quando vazio, empty via NenhumTerceiroInformado com transition) -->
    <b-table
      hover
      striped
      bordered
      responsive="sm"
      :fields="fields"
      :busy="isLoading"
      :items="terceiros"
      :show-empty="!isLoading"
      table-class="terceiros-datatables"
      thead-class="terceiros-datatables__head"
      tbody-tr-class="terceiros-datatables__row"
      class="terceiros-datatables datatables d-none d-md-table w-100"
    >
      <template #empty>
        <transition name="fade" appear>
          <NenhumTerceiroInformado />
        </transition>
      </template>
      <template #emptyfiltered>
        <div class="text-center text-muted py-3">
          Nenhum resultado encontrado.
        </div>
      </template>
      <template #table-busy>
        <div class="text-center text-success my-2">
          <b-spinner class="align-middle" />
        </div>
      </template>
      <template #cell(nome)="data">
        <span class="terceiros-envolvidos__name">
          {{ data.item.nome || "-" }}
        </span>
      </template>

      <template #cell(telefone)="data">
        {{ formatarTelefone(data.item.telefone) }}
      </template>

      <template #cell(cpf)="data">
        {{ formatarCpfCnpj(data.item.cpf) }}
      </template>

      <template #cell(veiculo)="data">
        {{ formatarVeiculoPlaca(data.item.veiculo) }}
      </template>

      <template #cell(lesao)="data">
        <span
          v-if="temInformacao(data.item.lesao, true)"
          :class="getLesaoBadgeClass(labelInformacao(data.item.lesao, true))"
        >
          <span class="lesao-badge__dot"></span>
          {{ labelInformacao(data.item.lesao, true) }}
        </span>
        <span v-else class="terceiros-envolvidos__sem-info">-</span>
      </template>

      <template #cell(culpado)="data">
        <span
          v-if="temInformacao(data.item.culpado)"
          :class="getCulpadoBadgeClass(labelInformacao(data.item.culpado))"
        >
          <span class="culpado-badge__dot"></span>
          {{ labelInformacao(data.item.culpado) }}
        </span>
        <span v-else class="terceiros-envolvidos__sem-info">-</span>
      </template>

      <template #cell(custos)="data">
        <span
          v-if="temInformacao(data.item.custos)"
          :class="getCustosBadgeClass(labelInformacao(data.item.custos))"
        >
          <span class="custos-badge__dot"></span>
          {{ labelInformacao(data.item.custos) }}
        </span>
        <span v-else class="terceiros-envolvidos__sem-info">-</span>
      </template>

      <template #cell(acoes)="data">
        <div v-if="!modoVisualizacao" class="terceiros-envolvidos__actions">
          <b-button
            size="sm"
            variant="primary"
            data-tooltip="Editar"
            data-tooltip-location="left"
            :disabled="isLoading"
            @click="$emit('editar', data.item, data.index)"
          >
            <i class="mdi mdi-pencil"></i>
          </b-button>
          <b-button
            size="sm"
            variant="danger"
            data-tooltip="Excluir"
            data-tooltip-location="left"
            :disabled="isLoading"
            @click="$emit('remover', data.index)"
          >
            <i class="mdi mdi-delete"></i>
          </b-button>
        </div>
        <!-- Modo visualização: apenas Visualizar – mesmo estilo de RelatoOcorrenciaGrid.vue:37 -->
        <div v-else class="terceiros-envolvidos__actions">
          <b-button
            size="sm"
            variant="primary"
            data-tooltip="Visualizar"
            data-tooltip-location="left"
            :disabled="isLoading"
            @click="$emit('visualizar-terceiro', data.item, data.index)"
          >
            <i class="mdi mdi-eye"></i>
          </b-button>
        </div>
      </template>
    </b-table>

    <!-- Cards Mobile - grid sempre renderizada, transição só no conteúdo vazio/lista.
      O wrapper em card existe só no estado vazio; com cards, vão soltos. -->
    <div
      class="card-ocorrencia-list d-block d-md-none"
      :class="{ 'card-ocorrencia-list--vazio': isEmpty && !isLoading }"
    >
      <transition name="fade" mode="out-in">
        <div v-if="isEmpty && !isLoading" key="vazio-mobile">
          <NenhumTerceiroInformado />
        </div>
        <div v-else key="lista-mobile">
          <div
            v-for="(terceiro, index) in terceiros"
            :key="terceiro.id || index"
            class="card-ocorrencia"
            :class="
              `card-ocorrencia--${
                labelInformacao(terceiro.lesao, true) === 'Não' ||
                ehSemInformacao(terceiro.lesao) ||
                !terceiro.lesao
                  ? 'primary'
                  : 'danger'
              }`
            "
          >
            <div class="card-ocorrencia__header">
              <div v-if="terceiro.veiculo" class="card-ocorrencia__title">
                <span class="card-ocorrencia__placa">{{
                  formatarVeiculoPlaca(terceiro.veiculo)
                }}</span>
              </div>
              <span
                v-if="temInformacao(terceiro.lesao, true)"
                :class="
                  getLesaoBadgeClass(labelInformacao(terceiro.lesao, true))
                "
              >
                <span class="lesao-badge__dot"></span>
                {{ labelInformacao(terceiro.lesao, true) }}
              </span>
              <span v-else class="terceiros-envolvidos__sem-info">-</span>
            </div>

            <div class="card-ocorrencia__projeto">
              <span class="card-ocorrencia__projeto-label">Nome</span>
              <span class="card-ocorrencia__projeto-value">{{
                terceiro.nome || "-"
              }}</span>
            </div>

            <div class="card-ocorrencia__grid">
              <div class="card-ocorrencia__field">
                <span class="card-ocorrencia__field-label">Telefone</span>
                <span class="card-ocorrencia__field-value">{{
                  formatarTelefone(terceiro.telefone)
                }}</span>
              </div>
              <div class="card-ocorrencia__field card-ocorrencia__field--right">
                <span class="card-ocorrencia__field-label">CPF/CNPJ</span>
                <span class="card-ocorrencia__field-value">{{
                  formatarCpfCnpj(terceiro.cpf) || "-"
                }}</span>
              </div>
              <div class="card-ocorrencia__field">
                <span class="card-ocorrencia__field-label">Culpado(a)</span>
                <span class="card-ocorrencia__field-value"
                  ><span
                    v-if="temInformacao(terceiro.culpado)"
                    :class="
                      getCulpadoBadgeClass(labelInformacao(terceiro.culpado))
                    "
                    ><span class="culpado-badge__dot"></span
                    >{{ labelInformacao(terceiro.culpado) }}</span
                  >
                  <span v-else class="terceiros-envolvidos__sem-info">-</span>
                </span>
              </div>
              <div class="card-ocorrencia__field card-ocorrencia__field--right">
                <span class="card-ocorrencia__field-label"
                  >Arcar com custos</span
                >
                <span class="card-ocorrencia__field-value"
                  ><span
                    v-if="temInformacao(terceiro.custos)"
                    :class="
                      getCustosBadgeClass(labelInformacao(terceiro.custos))
                    "
                    ><span class="custos-badge__dot"></span
                    >{{ labelInformacao(terceiro.custos) }}</span
                  >
                  <span v-else class="terceiros-envolvidos__sem-info">-</span>
                </span>
              </div>
            </div>

            <div v-if="!modoVisualizacao" class="card-ocorrencia__acoes">
              <button
                class="card-ocorrencia__btn card-ocorrencia__btn--editar"
                :disabled="isLoading"
                @click="$emit('editar', terceiro, index)"
              >
                <i class="mdi mdi-pencil"></i>
                Editar
              </button>
              <button
                class="card-ocorrencia__btn card-ocorrencia__btn--danger"
                :disabled="isLoading"
                @click="$emit('remover', index)"
              >
                <i class="mdi mdi-delete"></i>
                Excluir
              </button>
            </div>
            <button
              v-else
              class="card-ocorrencia__btn"
              :disabled="isLoading"
              @click="$emit('visualizar-terceiro', terceiro, index)"
            >
              <i class="mdi mdi-eye"></i>
              Visualizar
            </button>
          </div>
        </div>
      </transition>
      <!-- Mobile: botão dentro do card, abaixo da mensagem/lista -->
      <div
        v-if="!modoVisualizacao"
        class="terceiros-envolvidos__include terceiros-envolvidos__include--mobile"
      >
        <b-button
          variant="success"
          :disabled="isLoading"
          @click="$emit('novo')"
          data-tooltip-location="left"
          data-tooltip="Incluir Envolvido"
          class="terceiros-envolvidos__include-button"
        >
          <i
            class="mdi mdi-account-plus-outline me-2 font-size-17 font-bold"
          ></i>
          <span>Incluir Envolvido</span>
        </b-button>
      </div>
    </div>
  </div>
</template>

<script lang="js">
import NenhumTerceiroInformado from "./NenhumTerceiroInformado.vue";
import { aplicarMascaraTelefone } from "../../masks/telefone.mask.js";
import { aplicarMascaraCpfCnpj } from "../../masks/cpfCnpj.mask.js";
import { aplicarMascaraPlaca } from "../../masks/placa.mask.js";
import { getLesaoBadgeClass, getCulpadoBadgeClass, getCustosBadgeClass } from "../../../configs/terceirosBadges.config.js";

/**
 * Listagem de terceiros envolvidos (tabela desktop `b-table` + cards mobile).
 * Renderiza badges de lesão/culpado/custos via `terceirosBadges.config.js` e
 * estilos `badge-pill` (`_terceiros.scss` / `_variables.scss`).
 *
 * Modos:
 * - Cadastro (`inject modoVisualizacao=false`): coluna `Ações` com `b-button` `primary` (Editar) e `danger` (Excluir) + botão `Incluir Envolvido`; cards com `Editar` azul.
 * - Visualização (`inject modoVisualizacao=true` via `VisualizarRelatoOcorrenciaContainer`): coluna `Ações` com `b-button` `primary` `mdi-eye` (Visualizar) + `table-class="terceiros-datatables"` com `font-size:12px`; cards com `Visualizar`.
 *
 * Campos opcionais sem informação exibem "-"; a opção "Sem informações" de
 * lesão é preservada como uma escolha válida na grid.
 *
 * @prop {Array<EnvolvidoView|TerceiroForm>} terceiros - lista de terceiros (`nome`, `telefone`, `cpf`, `veiculo`, `lesao`/`culpado`/`custos` como `{value,label}`)
 * @prop {boolean} [isLoading=false] - exibe spinner `b-table` e desabilita ações (`:disabled="isLoading"`)
 *
 * @inject {boolean} modoVisualizacao - controla colunas/ações e desabilita edição
 *
 * @emits novo - solicita criação (`EnvolvimentoTerceiros.novo()`)
 * @emits editar {Object} terceiro, {number} index - solicita edição (bloqueado em visualização)
 * @emits remover {number} index - solicita exclusão (bloqueado em visualização)
 * @emits visualizar-terceiro {Object} terceiro, {number} index - solicita detalhe somente-leitura (somente em visualização, tratado por `EnvolvimentoTerceiros.abrirDetalheTerceiro`)
 */
export default {
  name: "ListaTerceiros",

  components: {
    NenhumTerceiroInformado
  },

  inject: {
    modoVisualizacao: {
      type: Boolean,
      default: false
    }
  },

  props: {
    terceiros: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    isEmpty() {
      return !this.isLoading && (!this.terceiros || this.terceiros.length === 0);
    },
    fields() {
      const base = [
        {
          key: "nome",
          label: "Nome",
          sortable: true,
          tdClass: "align-middle"
        },
        {
          sortable: true,
          key: "telefone",
          label: "Telefone",
          tdClass: "align-middle"
        },
        {
          key: "cpf",
          sortable: true,
          label: "CPF/CNPJ",
          tdClass: "align-middle text-center"
        },
        {
          key: "veiculo",
          sortable: true,
          label: "Veículo (Placa)",
          tdClass: "align-middle text-center"
        },
        {
          key: "lesao",
          label: "Lesão",
          sortable: true,
          tdClass: "align-middle text-center"
        },
        {
          key: "culpado",
          sortable: true,
          label: "Culpado(a)",
          tdClass: "align-middle text-center"
        },
        {
          key: "custos",
          sortable: true,
          label: "Arcar Com Custos",
          tdClass: "align-middle text-center"
        },
        {
          key: "acoes",
          label: "Ações",
          sortable: false,
          tdClass: "text-center align-middle"
        }
      ];
      return base;
    }
  },

  methods: {
    getLesaoBadgeClass(label) {
      return getLesaoBadgeClass(label);
    },
    getCulpadoBadgeClass(label) {
      return getCulpadoBadgeClass(label);
    },
    getCustosBadgeClass(label) {
      return getCustosBadgeClass(label);
    },
    formatarTelefone(valor) {
      if (!valor) return "-";
      const mascarado = aplicarMascaraTelefone(String(valor));
      return mascarado || "-";
    },
    formatarCpfCnpj(valor) {
      const documento =
        valor && typeof valor === "object"
          ? valor.cpfCnpj ?? valor.nrCpfCnpj ?? valor.value ?? valor.label
          : valor;
      if (documento == null || String(documento).trim() === "") return "-";
      const mascarado = aplicarMascaraCpfCnpj(String(documento));
      return mascarado || "-";
    },
    /**
     * @param {*} valor
     * @returns {string}
     */
    formatarVeiculoPlaca(valor) {
      if (!valor) return "-";
      const texto = String(valor);
      const placa = texto.match(/[A-Z]{3}-?[0-9][A-Z0-9][0-9]{2}/i);
      if (!placa) return texto;
      return texto.replace(placa[0], aplicarMascaraPlaca(placa[0]));
    },

    /**
     * Extrai o texto de um campo de opção (`{ value, label }`) ou string.
     * Valores vazios são ausência de dado; a opção de lesão pode preservar
     * explicitamente o rótulo "Sem informações".
     * @param {{label?: string}|string|null|undefined} valor
     * @param {boolean} incluirSemInformacao
     * @returns {string}
     */
    labelInformacao(valor, incluirSemInformacao = false) {
      if (!valor) return "";
      const label = typeof valor === "object" ? valor.label : valor;
      if (label == null) return "";
      const texto = String(label).trim();
      const normalizado = texto
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      if (
        !texto ||
        (!incluirSemInformacao && normalizado.startsWith("sem informacao"))
      ) {
        return "";
      }
      return texto;
    },

    ehSemInformacao(valor) {
      const texto = this.labelInformacao(valor, true)
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      return texto.startsWith("sem informacao");
    },

    /** @param {{label?: string}|string|null|undefined} valor
     * @returns {boolean}
     */
    temInformacao(valor, incluirSemInformacao = false) {
      return this.labelInformacao(valor, incluirSemInformacao) !== "";
    }
  }
};
</script>

<style lang="scss">
@import "../../../styles/_main";
@import "../../../styles/components/_grid";
@import "../../../styles/components/_terceiros";
@import "../../../styles/layout/_transitions";
</style>
